import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TargetIcon, HomeIcon, CircleDotIcon, PenLineIcon, PlusIcon, SearchIcon, MapPinIcon, XIcon, ChevronDownIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { RADIUS_OPTIONS, SECTORS, WINDSOR_ADDRESSES, type AddressEntry, type Coord } from '../../data/communications';
import { getPlaceLabel } from '../../utils/audience';
import type { AudiencePlace, AudiencePlaceKind } from '../../types/composer';
const PLACE_ICONS: Record<AudiencePlaceKind, typeof TargetIcon> = {
  sector: TargetIcon,
  address: HomeIcon,
  radius: CircleDotIcon,
  area: PenLineIcon
};
type AddMode = 'sector' | 'address' | 'area';
interface AudiencePlacesProps {
  places: AudiencePlace[];
  onChange: (places: AudiencePlace[]) => void;
  onToggleSector: (sectorId: string) => void;
  drawing: boolean;
  draftPoints: Coord[];
  onStartDrawing: () => void;
  onCancelDrawing: () => void;
  onFinishDrawing: () => void;
}

/**
 * "Lieu" builder: added places as chips, and a single adder that only
 * opens once the user picks what kind of place to add.
 */
export function AudiencePlaces({
  places,
  onChange,
  onToggleSector,
  drawing,
  draftPoints,
  onStartDrawing,
  onCancelDrawing,
  onFinishDrawing
}: AudiencePlacesProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [mode, setMode] = useState<AddMode | null>(null);

  // Address adder
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<AddressEntry | null>(null);
  const [radius, setRadius] = useState<string>('exact');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const matches = query ? WINDSOR_ADDRESSES.filter(a => a.label.toLowerCase().includes(query.toLowerCase())) : WINDSOR_ADDRESSES;
  const isAddressUsed = (label: string) => places.some(p => p.kind === 'address' && p.label === label && radius === 'exact');
  const addAddress = () => {
    if (!picked) return;
    const id = `pl-${Date.now()}`;
    const place: AudiencePlace = radius === 'exact' ? {
      id,
      kind: 'address',
      label: picked.label,
      coord: picked.coord
    } : {
      id,
      kind: 'radius',
      label: picked.label,
      coord: picked.coord,
      radius
    };
    onChange([...places, place]);
    setQuery('');
    setPicked(null);
  };
  const closeAdder = () => {
    if (drawing) onCancelDrawing();
    setMode(null);
    setQuery('');
    setPicked(null);
  };
  const remove = (id: string) => onChange(places.filter(p => p.id !== id));
  const selectedSectors = places.filter((p): p is Extract<AudiencePlace, {
    kind: 'sector';
  }> => p.kind === 'sector').map(p => p.sectorId);
  const modes: {
    id: AddMode;
    icon: typeof TargetIcon;
    label: string;
  }[] = [{
    id: 'sector',
    icon: TargetIcon,
    label: t('Secteur', 'District')
  }, {
    id: 'address',
    icon: HomeIcon,
    label: t('Adresse', 'Address')
  }, {
    id: 'area',
    icon: PenLineIcon,
    label: t('Zone sur la carte', 'Area on map')
  }];
  return <div className="space-y-3" data-id="element-3800" data-mp="329g7nlxwectn">
      {/* Places list */}
      {places.length > 0 && <ul className="flex flex-wrap gap-2" aria-label={t('Lieux ajoutés', 'Added places')} data-id="element-3801" data-mp="3pztsare5l9e7">
          <AnimatePresence initial={false} data-id="element-3802" data-mp="3u0vsbafw9ph8">
            {places.map((place, index) => {
          const Icon = PLACE_ICONS[place.kind];
          const color = place.kind === 'sector' ? SECTORS.find(s => s.id === place.sectorId)?.color : undefined;
          return <motion.li key={place.id} layout initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.12
          }} className="inline-flex items-center gap-2 max-w-full" data-id="element-3803" data-mp="24l67lcho9z56" data-mpi={index}>
                  {index > 0 && <span className="text-[11px] font-semibold uppercase text-slate-400" data-id="element-3804" data-mp="2b1ofptyw5yyv">
                      {t('ou', 'or')}
                    </span>}
                  <span className="inline-flex items-center gap-2 pl-2.5 pr-1 py-1 rounded-lg border border-slate-200 bg-white text-sm max-w-full" data-id="element-3805" data-mp="1ietuuvy5wxkv">
                  <span className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 text-white" style={{
                backgroundColor: color ?? '#4F46E5'
              }} data-id="element-3806" data-mp="1eadtn7zhuebz">
                    <Icon className="w-3 h-3" aria-hidden="true" data-id="element-3807" data-mp="zn15dbiaxuhw" />
                  </span>
                  <span className="font-medium text-slate-700 truncate" data-id="element-3808" data-mp="uo7snlaua7is">
                    {getPlaceLabel(place)[lang]}
                  </span>
                  <button type="button" onClick={() => remove(place.id)} aria-label={`${t('Retirer', 'Remove')} ${getPlaceLabel(place)[lang]}`} className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" data-id="element-3809" data-mp="2upo2wrx8gkt3">
                    <XIcon className="w-3.5 h-3.5" data-id="element-3810" data-mp="39h6co7zi1j40" />
                  </button>
                  </span>
                </motion.li>;
        })}
          </AnimatePresence>
        </ul>}

      {/* Adder — closed by default: just three compact "+" buttons */}
      {mode === null && places.length > 0 ? <div className="relative w-fit" ref={menuRef} data-id="element-3811" data-mp="2prvzp1r51c9l">
          <button type="button" onClick={() => setMenuOpen(o => !o)} aria-expanded={menuOpen} aria-haspopup="menu" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-[#4F46E5] rounded-lg hover:bg-indigo-50 transition-colors" data-id="element-3812" data-mp="2psp6u3u0e3s9">
            <PlusIcon className="w-4 h-4" data-id="element-3813" data-mp="1lhukd3pex1wh" />
            {t('Ajouter un lieu', 'Add a place')}
          </button>
          <AnimatePresence data-id="element-3814" data-mp="1gqcga6s3el7a">
            {menuOpen && <motion.ul role="menu" initial={{
          opacity: 0,
          y: -4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.12
        }} className="absolute left-0 z-30 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg py-1" data-id="element-3815" data-mp="1gorlqh5s329f">
                {modes.map(m => {
            const Icon = m.icon;
            return <li key={m.id} data-id="element-3816" data-mp="suhx0rbfgq24">
                      <button type="button" role="menuitem" onClick={() => {
                setMenuOpen(false);
                setMode(m.id);
                if (m.id === 'area') onStartDrawing();
              }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-indigo-50 text-left" data-id="element-3817" data-mp="2s61410d67o95">
                        <Icon className="w-4 h-4 text-slate-400" data-id="element-3818" data-mp="1m9oio7ov7r6g" />
                        {m.label}
                      </button>
                    </li>;
          })}
              </motion.ul>}
          </AnimatePresence>
        </div> : mode === null ? <div className="flex flex-wrap items-center gap-1" data-id="element-3819" data-mp="1halcul129gxf">
          {places.length === 0 && <span className="text-sm text-slate-500 mr-1" data-id="element-3820" data-mp="13f176racev7y">{t('Ajouter :', 'Add:')}</span>}
          {modes.map(m => {
        const Icon = m.icon;
        return <button key={m.id} type="button" onClick={() => {
          setMode(m.id);
          if (m.id === 'area') onStartDrawing();
        }} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-[#4F46E5] rounded-lg hover:bg-indigo-50 transition-colors" data-id="element-3821" data-mp="2xrr0p7wsu2et">
                <PlusIcon className="w-3.5 h-3.5" data-id="element-3822" data-mp="10u1qn2039l1c" />
                <Icon className="w-3.5 h-3.5 opacity-70" aria-hidden="true" data-id="element-3823" data-mp="w21vdmuyrgb3" />
                {m.label}
              </button>;
      })}
        </div> : <motion.div initial={{
      opacity: 0,
      y: -4
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.15
    }} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-3" data-id="element-3824" data-mp="19v00i0x7kmru">
          <div className="flex items-center justify-between" data-id="element-3825" data-mp="1es05s9m8hlhw">
            <span className="text-sm font-medium text-slate-700" data-id="element-3826" data-mp="3lipu19fv6ibs">
              {mode === 'sector' ? t('Choisir des secteurs', 'Pick districts') : mode === 'address' ? t('Ajouter une adresse', 'Add an address') : t('Tracer une zone', 'Trace an area')}
            </span>
            <button type="button" onClick={closeAdder} aria-label={t('Fermer', 'Close')} className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-white transition-colors" data-id="element-3827" data-mp="1w2itmwqn8bat">
              <XIcon className="w-4 h-4" data-id="element-3828" data-mp="1m7qsxk1rl8bt" />
            </button>
          </div>

          {mode === 'sector' && <div className="flex flex-wrap gap-2" data-id="element-3829" data-mp="2eubo6vld24kn">
              {SECTORS.map(s => {
          const active = selectedSectors.includes(s.id);
          return <button key={s.id} type="button" onClick={() => onToggleSector(s.id)} aria-pressed={active} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${active ? 'bg-white border-[#4F46E5] text-[#4F46E5]' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`} data-id="element-3830" data-mp="2a0z0cnoia1sy">
                    <span className="w-2 h-2 rounded-full" style={{
              backgroundColor: s.color
            }} data-id="element-3831" data-mp="3f172c0etzdy5" />
                    {s.label[lang]}
                    {active && <CheckIcon className="w-3.5 h-3.5" data-id="element-3832" data-mp="3jyyrimo3clyn" />}
                  </button>;
        })}
            </div>}

          {mode === 'address' && <div className="flex flex-col sm:flex-row gap-2" data-id="element-3833" data-mp="3t06kcp7u8ulc">
              <div className="relative flex-1" ref={boxRef} data-id="element-3834" data-mp="1odaow8sm9j4">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-3835" data-mp="6mf4ab3uhr8u" />
                <input type="text" autoFocus value={query} onChange={e => {
            setQuery(e.target.value);
            setPicked(null);
            setOpen(true);
          }} onFocus={() => setOpen(true)} onKeyDown={e => {
            if (e.key === 'Enter' && picked) {
              e.preventDefault();
              addAddress();
            }
          }} placeholder={t('Rechercher une adresse…', 'Search for an address…')} aria-label={t('Rechercher une adresse', 'Search for an address')} className="w-full bg-white border border-slate-200 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-3836" data-mp="blrr4vupuybf" />
                {open && matches.length > 0 && <ul className="absolute z-[1100] mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto py-1" data-id="element-3837" data-mp="ejypb4p3wkoy">
                    {matches.map(a => <li key={a.label} data-id="element-3838" data-mp="2gf90vfwgtro0">
                        <button type="button" onClick={() => {
                setPicked(a);
                setQuery(a.label);
                setOpen(false);
              }} className="w-full flex items-center gap-2 text-left px-3 py-2 text-sm text-slate-700 hover:bg-indigo-50" data-id="element-3839" data-mp="vvm01pks3nzn">
                          <MapPinIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-3840" data-mp="d430xttvku2w" />
                          {a.label}
                        </button>
                      </li>)}
                  </ul>}
              </div>
              <div className="relative sm:w-52" data-id="element-3841" data-mp="85jhc02r7udp">
                <select value={radius} onChange={e => setRadius(e.target.value)} aria-label={t('Portée', 'Reach')} className="w-full bg-white border border-slate-200 rounded-lg pl-3 pr-9 py-2 text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-3842" data-mp="861j1c86r0t0">
                  <option value="exact" data-id="element-3843" data-mp="3tp5nik21bqny">{t('Cette adresse seulement', 'This address only')}</option>
                  {RADIUS_OPTIONS.map(r => <option key={r.value} value={r.value} data-id="element-3844" data-mp="3kn663qn3ykkh">
                      {t('Rayon de ', 'Radius of ')}
                      {r.label[lang]}
                    </option>)}
                </select>
                <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-3845" data-mp="3ax4h8wmxky4i" />
              </div>
              <button type="button" onClick={addAddress} disabled={!picked || picked !== null && isAddressUsed(picked.label)} className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-3846" data-mp="35w71eqpe8g66">
                <PlusIcon className="w-4 h-4" data-id="element-3847" data-mp="1cfkh425eo9xs" /> {t('Ajouter', 'Add')}
              </button>
            </div>}

          {mode === 'area' && <div className="flex items-center justify-between gap-3 flex-wrap" data-id="element-3848" data-mp="1hf5iskbymwe7">
              <p className="text-sm text-slate-500" data-id="element-3849" data-mp="1lf7kuullxru0">
                {t(`Cliquez sur la carte — ${draftPoints.length} point(s), 3 minimum.`, `Click the map — ${draftPoints.length} point(s), 3 minimum.`)}
              </p>
              <button type="button" onClick={() => {
          onFinishDrawing();
          setMode(null);
        }} disabled={draftPoints.length < 3} className="px-3 py-1.5 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-3850" data-mp="xq4v0zgtksof">
                {t('Ajouter la zone', 'Add area')}
              </button>
            </div>}
        </motion.div>}
    </div>;
}